import React, { useState } from 'react';
import { Linkedin, Mail, ArrowUp, X } from 'lucide-react';
import { Button } from './ui/button';
import { contactData } from '../data/mockData';

const Footer = () => {
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-bg-card border-t border-border-light">
      <div className="container mx-auto px-6">
        {/* Main Footer Content */}
        <div className="py-16">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Brand Column - Left Side */}
            <div className="md:col-span-2">
              <div className="mb-6">
                <h3 className="text-3xl font-black text-brand-primary font-grotesk uppercase mb-2">
                  Gideon Metre
                </h3>
                <p className="text-text-secondary mb-4">
                  Professional Portfolio & Insights
                </p>
              </div>

              {/* Professional Connect */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => window.open(contactData.linkedin, '_blank')}
                  className="btn-base btn-secondary btn-sm group"
                >
                  <Linkedin size={16} className="mr-2 group-hover:scale-110 transition-transform" />
                  Connect on LinkedIn
                </Button>
                <Button
                  onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                  className="btn-base btn-primary btn-sm group"
                >
                  <Mail size={16} className="mr-2 group-hover:translate-x-1 transition-transform" />
                  Get in Touch
                </Button>
              </div>
            </div>

            {/* Quick Links - Far Right Side */}
            <div className="flex justify-end">
              <div>
              <h4 className="heading-6 text-text-primary mb-6">Quick Links</h4>
              <ul className="space-y-3">
                <li>
                  <button
                    onClick={() => document.getElementById('personal').scrollIntoView({ behavior: 'smooth' })}
                    className="text-text-secondary hover:text-brand-primary transition-colors duration-300"
                  >
                    About Me
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => document.getElementById('projects').scrollIntoView({ behavior: 'smooth' })}
                    className="text-text-secondary hover:text-brand-primary transition-colors duration-300"
                  >
                    Tech Projects
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => document.getElementById('blog').scrollIntoView({ behavior: 'smooth' })}
                    className="text-text-secondary hover:text-brand-primary transition-colors duration-300"
                  >
                    Blog & Insights
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                    className="text-text-secondary hover:text-brand-primary transition-colors duration-300"
                  >
                    Contact
                  </button>
                </li>
              </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Footer */}
        <div className="border-t border-border-light py-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <p className="text-text-secondary text-sm">
                © {currentYear} Gideon Metre. All Rights Reserved.
              </p>
            </div>

            <div className="flex items-center space-x-6">
              <button 
                onClick={() => setShowPrivacyModal(true)}
                className="text-text-secondary hover:text-brand-primary transition-colors duration-300 text-sm"
              >
                Privacy Policy
              </button>
              <button 
                onClick={() => setShowTermsModal(true)}
                className="text-text-secondary hover:text-brand-primary transition-colors duration-300 text-sm"
              >
                Terms of Service
              </button>
              
              {/* Back to Top Button */}
              <Button
                onClick={scrollToTop}
                size="sm"
                variant="outline"
                className="btn-secondary"
              >
                <ArrowUp size={16} className="mr-2" />
                Top
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Back to Top (for mobile) */}
      <button
        onClick={scrollToTop}
        className="fixed bottom-6 right-6 w-12 h-12 bg-brand-primary text-text-inverse rounded-full shadow-lg hover:scale-110 transition-transform duration-300 z-40 md:hidden"
      >
        <ArrowUp size={20} className="mx-auto" />
      </button>

      {/* Privacy Policy Modal */}
      {showPrivacyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="glass-card max-w-4xl max-h-[80vh] overflow-y-auto p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-text-primary">Privacy Policy</h2>
              <button 
                onClick={() => setShowPrivacyModal(false)}
                className="text-text-secondary hover:text-text-primary"
              >
                <X size={24} />
              </button>
            </div>
            <div className="space-y-4 text-text-secondary">
              <p><strong>Last updated:</strong> September 2025</p>
              
              <h3 className="text-lg font-semibold text-text-primary">Information We Collect</h3>
              <p>This portfolio website may collect basic visitor information including:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Contact information when you use the contact form</li>
                <li>Basic analytics data to improve website performance</li>
                <li>Cookies for website functionality and user experience</li>
              </ul>

              <h3 className="text-lg font-semibold text-text-primary">How We Use Information</h3>
              <p>Information collected is used to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Respond to your inquiries and communication</li>
                <li>Improve website functionality and user experience</li>
              </ul>

              <h3 className="text-lg font-semibold text-text-primary">Data Protection</h3>
              <p>We implement appropriate security measures to protect your personal information. Your data is not sold, traded, or transferred to third parties without your consent, except as described in this policy.</p>

              <h3 className="text-lg font-semibold text-text-primary">Contact</h3>
              <p>If you have questions about this Privacy Policy, please contact through the provided contact form on this website.</p>
            </div>
          </div>
        </div>
      )}

      {/* Terms of Service Modal */}
      {showTermsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="glass-card max-w-4xl max-h-[80vh] overflow-y-auto p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-text-primary">Terms of Service</h2>
              <button 
                onClick={() => setShowTermsModal(false)}
                className="text-text-secondary hover:text-text-primary"
              >
                <X size={24} />
              </button>
            </div>
            <div className="space-y-4 text-text-secondary">
              <p><strong>Last updated:</strong> September 2025</p>
              
              <h3 className="text-lg font-semibold text-text-primary">Website Usage</h3>
              <p>This portfolio website is provided for informational purposes. By accessing and using this website, you agree to comply with these terms.</p>

              <h3 className="text-lg font-semibold text-text-primary">Content</h3>
              <p>All content on this website, including but not limited to text, images, and design elements, is the property of Gideon Metre and is protected by applicable copyright laws.</p>

              <h3 className="text-lg font-semibold text-text-primary">Professional Information</h3>
              <p>The information presented reflects professional experience and achievements. While every effort is made to ensure accuracy, this website serves as a general portfolio and should not be considered as professional advice.</p>

              <h3 className="text-lg font-semibold text-text-primary">Contact and Communication</h3>
              <p>Communication through this website's contact forms is for professional networking and collaboration purposes. Response times may vary based on availability.</p>

              <h3 className="text-lg font-semibold text-text-primary">Disclaimer</h3>
              <p>This website is provided "as is" without warranties of any kind. The owner reserves the right to modify these terms and website content at any time.</p>
            </div>
          </div>
        </div>
      )}
    </footer>
  );
};

export default Footer;